<template>
    <div class="quote-history-section">

        <div v-if="quotesHistory && Object.keys(quotesHistory).length > 0" class="history-list">
            <div v-for="(venue, index) in groupedHistory" :key="`venue-${index}`" class="venue-history-group">
                <!-- Venue Header -->
                <div class="venue-header">
                    <h4 class="venue-name">{{ venue.venueName }}</h4>
                    <div class="venue-actions" v-if="hasActiveQuote(venue) || !! venueInfo(venue)">
                        <button v-if="hasActiveQuote(venue)" class="btn btn-outline-primary btn-xs edit-quote-btn"
                            @click="editVenueQuote(venue)" title="Edit Quote">
                            <i class="fa fa-edit"></i> Edit
                        </button>
                        <cancel-quote-request v-if="leadEvent && !! venueInfo(venue)" :lead-event="leadEvent"
                            :venue="venueInfo(venue)" @update="$emit('quote-request-cancelled')" />
                    </div>
                </div>

                <!-- History Items for this venue -->
                <div class="history-items">
                    <div v-for="(historyItem, historyIndex) in venue.history" :key="`history-${historyIndex}`"
                        class="history-item relative">
                        <div class="history-icon">
                            <i :class="getHistoryIcon(historyItem.type)"></i>
                        </div>
                        <div class="history-event">
                            <span>{{ historyItem.event }}</span>
                            <div class="lead-comment fa fa-commenting-o"
                                v-if="historyItem.type == 'quote_change_requested' && historyItem.change_details?.length">
                            </div>
                            <div class="change-request-details" v-if="historyItem.type == 'quote_change_requested'">
                                <small>{{ historyItem.change_details }}</small>
                            </div>
                        </div>
                        <div class="history-timestamp">{{ formatTimestamp(historyItem.timestamp) }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="no-history">
            <p class="text-muted">No quote history available.</p>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { Message } from 'element-ui';
import CancelQuoteRequest from '@/views/Widgets/LeadEventDetails/CancelQuoteRequest.vue';

export default {
    name: "QuoteHistoryDisplay",
    components: {
        CancelQuoteRequest,
    },
    props: {
        quotesHistory: {
            type: [Object, Array],
            default: () => ({})
        },
        leadEvent: {
            type: Object,
            default: null,
        },
        venuesInfo: {
            type: Object,
            default: () => ({}),
        }
    },
    computed: {
        groupedHistory() {
            if (!this.quotesHistory) {
                return [];
            }

            // Handle case where quotesHistory is an object with venue IDs as keys
            if (typeof this.quotesHistory === 'object' && ! Array.isArray(this.quotesHistory)) {
                const venueGroups = [];

                // Iterate through each venue ID key
                Object.keys(this.quotesHistory).forEach(venueId => {
                    const venueData = this.quotesHistory[venueId];
                    if (venueData && venueData.venue_id) {
                        const history = venueData.history && Array.isArray(venueData.history)
                            ? venueData.history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                            : [];

                        // Only include venues that have history items
                        if (history.length > 0) {
                            venueGroups.push({
                                venueId: venueData.venue_id,
                                isCustomVenue: venueData.is_custom_venue || false,
                                venueName: venueData.venue_name || `Venue ${venueData.venue_id}`,
                                history: history
                            });
                        }
                    }
                });

                return venueGroups;
            }

            // Handle legacy case where quotesHistory is an array
            if (Array.isArray(this.quotesHistory)) {
                const venueGroups = {};

                this.quotesHistory.forEach(quote => {
                    const key = quote.is_custom_venue ? `custom_venue_${quote.venue_id}` : `venue_${quote.venue_id}`;
                    const venueId = quote.venue_id;
                    const venueName = quote.venue_name || `Venue ${venueId}`;

                    if (!venueGroups[key]) {
                        venueGroups[key] = {
                            venueId,
                            venueName,
                            history: []
                        };
                    }

                    // Add history items for this quote
                    if (quote.history && Array.isArray(quote.history)) {
                        venueGroups[key].history.push(...quote.history);
                    }
                });

                // Convert to array and sort history within each venue, only include venues with history
                return Object.values(venueGroups).map(venue => ({
                    ...venue,
                    history: venue.history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                })).filter(venue => venue.history.length > 0); // Only include venues with history items
            }

            return [];
        }
    },
    methods: {
        venueInfo(venue) {
            const key = venue.isCustomVenue ? `custom_venue_${venue.venueId}` : `venue_${venue.venueId}`;
            return this.venuesInfo[key] || null;
        },
        hasActiveQuote(venue) {
            const key = venue.isCustomVenue ? `custom_venue_${venue.venueId}` : venue.venueId;
            const venueData = this.quotesHistory[key];

            return !!(venueData && venueData.active_quote);
        },

        editVenueQuote(venue) {
            const key = venue.isCustomVenue ? `custom_venue_${venue.venueId}` : venue.venueId;
            const venueData = this.quotesHistory[key];

            if (venueData?.active_quote) {
                venueData.active_quote.venue_id = venue.venueId;
                venueData.active_quote.is_for_custom_venue = venue.isCustomVenue || false;
                this.$emit('edit-quote', venueData.active_quote);
            } else {
                console.warn('No active quote found for venue:', key);
                Message.warning('No active quote found for this venue.');
            }
        },

        getHistoryIcon(event) {
            const iconMap = {
                'request': 'fa fa-bell text-info',
                'quote_published': 'fa fa-bullhorn text-success',
                'quote_sent': 'fa fa-check text-success',
                'quote_change_requested': 'fa fa-edit text-warning',
                'quote_created': 'fa fa-plus text-primary',
                'client_approved': 'fa fa-thumbs-up text-success',
                'quote_draft_updated': 'fa fa-info text-secondary'
            };
            return iconMap[event] || 'fa fa-circle text-muted';
        },

        formatTimestamp(timestamp) {
            if (!timestamp) return '';

            try {
                const momentTime = moment.utc(timestamp).local(); // Parse as UTC and convert to local
                const now = moment();
                // Compare calendar days, not 24-hour spans, so an evening timestamp isn't labelled "Today" the next morning
                const diffDays = now.startOf('day').diff(momentTime.clone().startOf('day'), 'days');

                // Format time in 12-hour AM/PM format
                const timeString = momentTime.format('h:mm A');

                if (diffDays === 0) {
                    return timeString + ' - Today';
                } else if (diffDays === 1) {
                    return timeString + ' - Yesterday';
                } else {
                    const dateString = momentTime.format('D MMM YYYY');
                    return timeString + ' - ' + dateString;
                }
            } catch (error) {
                console.warn('Error formatting timestamp:', error);
                return timestamp;
            }
        }
    }
};
</script>

<style scoped>
.quote-history-section h4 {
    margin-bottom: 20px;
}

.venue-history-group {
    margin-bottom: 30px;
}

.venue-header {
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.edit-quote-btn {
    opacity: 0.7;
    transition: all 0.3s ease;
    font-size: 10px;
    padding: 4px 8px;
}

.edit-quote-btn:hover {
    opacity: 1;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.venue-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

@media (max-width: 768px) {
    .venue-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .edit-quote-btn {
        align-self: flex-end;
    }

    .venue-actions {
        align-self: flex-end;
    }
}

.venue-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.history-items {
    padding-left: 0;
}

.history-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}

.history-item:last-child {
    border-bottom: none;
}

.history-icon {
    width: 25px;
    text-align: center;
    margin-right: 10px;
    flex-shrink: 0;
}

.history-icon i {
    font-size: 14px;
}

.history-event {
    position: relative;
    flex: 1;
    font-weight: 400;
    color: #333;

    .lead-comment {
        margin-left: 8px;
        font-size: 14px;
        color: #888;
        cursor: pointer;

        &:hover+.change-request-details {
            display: block;
            width: 200px;
            max-height: 200px;
            overflow-y: auto;
            background: #f9f9f9;
        }
    }

    .change-request-details {
        display: none;
        position: absolute;
        padding: 10px;
        bottom: 20px;
        left: 200px;
        font-size: 14px;
        color: #555;
        border-radius: 10px;
    }
}

.history-timestamp {
    font-size: 13px;
    color: #666;
    text-align: right;
    flex-shrink: 0;
}

.no-history {
    text-align: center;
    padding: 20px;
    color: #666;
}

.text-info {
    color: #17a2b8;
}

.text-success {
    color: #28a745;
}

.text-warning {
    color: #ffc107;
}

.text-secondary {
    color: #6c757d;
}

.history-icon i {
    border-radius: 40px;
    height: 17px;
    width: 17px;
    padding: 3px;
    font-size: 10px;
}

.history-icon i.text-info {
    background: #b9f4ff;
}

.history-icon i.text-primary {
    background: #a5c7f4;
}

.history-icon i.text-success {
    background: #bcf2db;
}

.history-icon i.text-warning {
    background: #ffdad1;
}

.history-icon i.text-secondary {
    background: #bec0c1;
}
</style>